/* One confirmed cart, serial writes, independent quantity intent. */
(function () {
  'use strict';
  function createCart(options) {
    const copy = value => JSON.parse(JSON.stringify(value));
    const props = item => item.properties || {};
    const plan = item => String(item.selling_plan_allocation?.selling_plan?.id || '');
    const equal = (a, b) => JSON.stringify(Object.keys(a).sort().map(k => [k, a[k]])) === JSON.stringify(Object.keys(b).sort().map(k => [k, b[k]]));
    let cart = copy(options.cart), records = [], nextId = 0, queue = [], running = false, blocked = false, lastError = null, errorVersion = 0;
    const drains = [];
    const pending = () => running || queue.length > 0 || records.some(r => r.timer || r.desired != null);
    function error(message, status) { const e = new Error(message); e.status = status; return e; }
    function same(item, other, properties) { return String(item.variant_id || item.id) === String(other.variant_id || other.id) && plan(item) === plan(other) && equal(props(item), properties || props(other)); }
    function accept(value, active) {
      if (!value || !Array.isArray(value.items) || !Number.isFinite(value.total_price)) throw error('Invalid basket response');
      const available = value.items.slice(), assigned = new Map();
      const activeFor = record => Array.isArray(active) ? active.find(a => a.record === record) : active?.record === record ? active : null;
      records.forEach(r => { const item = available.find(i => i.key === r.item.key); if (item) { assigned.set(r, item); available.splice(available.indexOf(item), 1); } });
      // Keys can change when Shopify recalculates discounts or properties. Never guess between ambiguous lines.
      records.filter(r => !assigned.has(r)).forEach(r => {
        const change = activeFor(r);
        if (change?.quantity === 0) return;
        const candidates = available.filter(i => same(i, r.item, change?.properties));
        if (candidates.length === 1) { assigned.set(r, candidates[0]); available.splice(available.indexOf(candidates[0]), 1); }
      });
      records.forEach(r => {
        const item = assigned.get(r);
        if (item && r.relativeIntent && r.desired != null && !activeFor(r)) r.desired = Math.max(0, r.desired + item.quantity - (r.present ? r.item.quantity : 0));
        r.present = !!item; if (item) r.item = copy(item);
      });
      available.forEach(item => records.push({ref: String(++nextId), item: copy(item), present: true, desired: null, revision: 0, waiters: []}));
      cart = copy(value);
    }
    function weightedProperties(record, quantity) {
      const original = props(record.item), weight = String(original.Quantity || '');
      if (!/^\s*[\d.]+\s*kg\s*$/i.test(weight)) return null;
      // The existing store uses 250 g units. Merge properties, including private and subscription metadata.
      return Object.assign({}, original, {Quantity: Math.max(0, Math.round((parseFloat(weight) + (quantity - record.item.quantity) * 0.25) * 1000) / 1000) + 'Kg'});
    }
    function view() {
      const result = copy(cart);
      result.items = cart.items.map(item => {
        const record = records.find(r => r.present && r.item.key === item.key), line = copy(item);
        line.qfc_ref = record.ref;
        if (record.desired != null) { line.quantity = record.desired; line.properties = weightedProperties(record, record.desired) || props(line); }
        return line;
      }).filter(item => item.quantity > 0);
      return result;
    }
    function publish() { options.publish?.(copy(cart), view(), {pending: !!pending(), blocked, error: lastError}); }
    function settle(record, failure) { record.waiters.splice(0).forEach(w => failure ? w.reject(failure) : w.resolve(copy(cart))); }
    function enqueueRecord(record) {
      clearTimeout(record.timer); record.timer = null;
      if (!record.queued && !record.inflight && record.desired != null) { record.queued = true; queue.push({record}); }
      void pump();
    }
    function failAll(failure) {
      records.forEach(r => { clearTimeout(r.timer); r.timer = null; r.desired = null; r.queued = false; settle(r, failure); });
      queue.splice(0).forEach(job => job.reject?.(failure));
    }
    async function reconcile(active) { accept(await options.request('cart.js'), active); }
    async function pump() {
      if (running) return;
      running = true;
      while (queue.length && !blocked) {
        const job = queue.shift(), record = job.record;
        const batch = [];
        let revision, target;
        try {
          if (record) {
            record.queued = false; record.inflight = true;
            revision = record.revision; target = record.desired;
            if (!record.present && !record.creatable) { if (target === 0) { record.desired = null; settle(record); continue; } throw error('Basket line changed. Please review your basket.', 409); }
            if (target === (record.present ? record.item.quantity : 0)) { record.desired = null; settle(record); continue; }
            const properties = weightedProperties(record, target);
            const active = {record, quantity: target, properties: properties || props(record.item)};
            if (!record.present) {
              while (queue[0]?.record?.creatable && !queue[0].record.present && queue[0].record.desired > 0) {
                const extra = queue.shift().record;
                extra.queued = false; extra.inflight = true;
                batch.push({record:extra, quantity:extra.desired, revision:extra.revision, properties:weightedProperties(extra, extra.desired) || props(extra.item)});
              }
              const changes = [active, ...batch];
              await addNow(changes.map(a => ({id:a.record.item.variant_id, quantity:a.quantity, properties:a.properties})), changes);
              for (const extra of batch) {
                if (extra.record.revision === extra.revision) extra.record.desired = null;
                if (!extra.record.present || extra.record.item.quantity !== extra.quantity) throw error('Shopify could not save the requested quantity.', 422);
              }
            } else {
              const payload = {id: record.item.key, quantity: target};
              if (properties && target > 0) payload.properties = properties;
              accept(await options.request('cart/change.js', payload), active);
            }
            if (record.revision === revision) record.desired = null;
            if ((record.present ? record.item.quantity : 0) !== target) throw error('Shopify could not save the requested quantity.', 422);
            if (record.desired == null) settle(record);
            batch.forEach(extra => { if (extra.record.desired == null) settle(extra.record); });
          } else {
            const result = await job.run(); job.resolve(result);
          }
        } catch (failure) {
          lastError = failure; errorVersion++;
          if (record) { record.desired = null; settle(record, failure); }
          batch.forEach(extra => { extra.record.desired = null; settle(extra.record, failure); });
          job.reject?.(failure);
          if (!(failure.status >= 400 && failure.status < 500)) blocked = true;
          // Do not replay an uncertain POST; finish reconciliation before another queued write.
          try { await reconcile(); } catch (readError) { blocked = true; }
          if (blocked) failAll(failure);
        } finally {
          if (record) {
            record.inflight = false;
            if (record.desired != null && !record.timer && !record.queued) { record.queued = true; queue.push({record}); }
          }
          batch.forEach(extra => {
            const r = extra.record; r.inflight = false;
            if (r.desired != null && !r.timer && !r.queued) { r.queued = true; queue.push({record:r}); }
          });
          publish();
        }
      }
      running = false; publish();
      if (!pending()) drains.splice(0).forEach(w => blocked || errorVersion !== w.version ? w.reject(lastError || error('Review basket')) : w.resolve(copy(cart)));
    }
    function recordFor(item) { return records.find(r => item.qfc_ref ? r.ref === item.qfc_ref : r.present && r.item.key === item.key); }
    function setQuantity(item, quantity, relative = false) {
      if (blocked) return Promise.reject(lastError);
      const record = recordFor(item);
      if (!record || !Number.isInteger(quantity) || quantity < 0) return Promise.reject(error('Invalid basket line or quantity', 400));
      lastError = null; record.desired = quantity; record.relativeIntent = relative; record.revision++;
      const promise = new Promise((resolve, reject) => record.waiters.push({resolve, reject}));
      clearTimeout(record.timer); record.timer = setTimeout(() => enqueueRecord(record), options.debounce ?? 120);
      publish(); return promise;
    }
    function step(item, delta) {
      const record = recordFor(item);
      return record ? setQuantity(item, Math.max(0, (record.desired ?? record.item.quantity) + delta), true) : Promise.reject(error('Basket line changed', 409));
    }
    function task(run) {
      if (blocked) return Promise.reject(lastError);
      lastError = null;
      const result = new Promise((resolve, reject) => queue.push({run, resolve, reject}));
      publish(); void pump(); return result;
    }
    async function addNow(items, active) {
      const result = await options.request('cart/add.js', {items});
      if (!Array.isArray(result.items) || !items.every(wanted => result.items.some(item => String(item.id) === String(wanted.id) && item.quantity >= wanted.quantity && plan(item) === String(wanted.selling_plan || '')))) throw error('Missing confirmed basket item');
      if (result.qfc_cart) accept(result.qfc_cart, active); else await reconcile(active);
      return result;
    }
    function flush() {
      const version = errorVersion;
      records.filter(r => r.timer).forEach(enqueueRecord);
      if (!pending()) return blocked ? Promise.reject(lastError) : Promise.resolve(copy(cart));
      return new Promise((resolve, reject) => drains.push({resolve, reject, version}));
    }
    accept(cart);
    const simpleLines = variant => cart.items.filter(i => String(i.variant_id || i.id) === String(variant) && !plan(i) && Object.keys(props(i)).every(k => k === 'Quantity'));
    function variantRecord(variant) {
      const lines = simpleLines(variant);
      return lines.length === 1 ? recordFor(lines[0]) : records.find(r => !r.present && r.creatable && String(r.item.variant_id) === String(variant));
    }
    function variantState(variant) {
      const record = variantRecord(variant);
      return {quantity: record ? (record.desired ?? (record.present ? record.item.quantity : 0)) : simpleLines(variant).reduce((n, i) => n + i.quantity, 0), pending: !!record && (record.desired != null || !!record.inflight)};
    }
    return {
      setQuantity, step, flush, pending: () => !!pending(), blocked: () => blocked,
      snapshot: () => copy(cart), view, variantState, render: publish,
      add: items => task(() => addNow(items)),
      attributes: payload => task(async () => {
        const sameAttributes = Object.entries(payload.attributes || {}).every(([key, value]) => String(cart.attributes?.[key] ?? '') === String(value ?? ''));
        const sameNote = !('note' in payload) || String(cart.note || '') === String(payload.note || '');
        if (sameAttributes && sameNote) return copy(cart);
        const result = await options.request('cart/update.js', payload); accept(result); return copy(cart);
      }),
      refresh: () => task(async () => { await reconcile(); return copy(cart); }),
      seed: value => { if (!pending()) { accept(value); publish(); } },
      stepVariant: (variant, delta, weighted) => {
        if (blocked) return Promise.reject(lastError);
        if (simpleLines(variant).length > 1) return Promise.reject(error('Choose the exact line in your basket.', 409));
        let record = variantRecord(variant);
        if (!record) {
          if (delta <= 0) return Promise.resolve(copy(cart));
          record = {ref: String(++nextId), item: {id: variant, variant_id: variant, key: 'pending:' + variant, quantity: 0, properties: weighted ? {Quantity:'0Kg'} : {}}, present:false, creatable:true, desired:null, revision:0, waiters:[]};
          records.push(record);
        }
        return setQuantity({qfc_ref:record.ref}, Math.max(0, (record.desired ?? (record.present ? record.item.quantity : 0)) + delta), true);
      }
    };
  }
  if (typeof module !== 'undefined' && module.exports) { module.exports = createCart; return; }
  const configElement = document.getElementById('qfc-sync-config');
  if (!configElement || window.QFCart) return;
  const config = JSON.parse(configElement.textContent), root = window.Shopify?.routes?.root || '/';
  let publishing = false, publishedCart = JSON.stringify(config.cart), feedbackTimer, pillFrame;
  const metrics = {reads:0, writes:0, bundled:0, lastRequestMs:0};
  async function request(path, payload) {
    const started = performance.now();
    metrics[payload ? 'writes' : 'reads']++;
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 20000);
    try {
      if (path === 'cart/add.js') payload = Object.assign({}, payload, {sections:['qf-cart-data'], sections_url:root + 'cart'});
      const response = await fetch(root + path, {method: payload ? 'POST' : 'GET', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, cache: 'no-store', signal: controller.signal, ...(payload ? {body: JSON.stringify(payload)} : {})});
      const result = await response.json();
      if (!response.ok) { const error = new Error(result.description || result.message || config.error); error.status = response.status; throw error; }
      if (path === 'cart/add.js' && result.sections?.['qf-cart-data']) {
        try {
          const data = new DOMParser().parseFromString(result.sections['qf-cart-data'], 'text/html').querySelector('[data-qfc-cart]');
          const fullCart = data && JSON.parse(data.textContent);
          if (Array.isArray(fullCart?.items) && Number.isFinite(fullCart.total_price)) { result.qfc_cart = fullCart; metrics.bundled++; }
        } catch (_) {}
      }
      return result;
    } finally { clearTimeout(timer); metrics.lastRequestMs = Math.round(performance.now() - started); configElement.dataset.qfcMetrics = JSON.stringify(metrics); }
  }
  const api = createCart({cart: config.cart, request, publish(cart, view, state) {
    publishing = true;
    try {
      const signature = JSON.stringify(cart), confirmedChanged = signature !== publishedCart;
      if (confirmedChanged) {
        publishedCart = signature;
        if (window.CartJS?.cart?.update) window.CartJS.cart.update(cart);
        window.cart = JSON.parse(JSON.stringify(cart));
        if (window.qfcUpdateFDB) window.qfcUpdateFDB(cart.total_price, document.getElementById('state')?.value || '');
        if (window.jQuery) window.jQuery(document).trigger('cart.requestComplete', [cart]);
        document.dispatchEvent(new CustomEvent('cart:updated', {detail: {cart}}));
      }
      if (window.vm?.cart) window.vm.cart = view;
      const table = document.getElementById('cartTable'); if (table) table.setAttribute('aria-busy', String(state.pending));
      const notice = document.getElementById('qfc-sync-status');
      if (notice) {
        notice.querySelector('[data-qfc-message]').textContent = state.error ? (state.blocked ? config.uncertain : config.error) : config.updating;
        notice.querySelector('a').hidden = !state.error;
        if (state.error || !state.pending) { clearTimeout(feedbackTimer); feedbackTimer = null; notice.hidden = !state.error; }
        else if (!feedbackTimer && notice.hidden) feedbackTimer = setTimeout(() => { feedbackTimer = null; notice.hidden = !api.pending(); }, 900);
      }
      let cardsChanged = false;
      document.querySelectorAll('[data-controller~="addtocart"]').forEach(element => {
        const variant = element.querySelector('[data-target="addtocart.variant"]')?.value || element.id.match(/^addToCart-(\d+)$/)?.[1];
        const choice = api.variantState(variant), count = choice.quantity;
        element.setAttribute('aria-busy', String(choice.pending));
        element.toggleAttribute('data-qfc-pending', choice.pending);
        const qty = element.querySelector('[data-target="addtocart.qty"]'); if (qty && Number(qty.value) !== count) { qty.value = count; cardsChanged = true; }
        const gram = element.querySelector('[data-target="addtocart.qty_gram"]'); if (gram) gram.value = count * 0.25;
      });
      if (cardsChanged && !pillFrame) pillFrame = requestAnimationFrame(() => { pillFrame = null; if (window.fixQtyButtons) window.fixQtyButtons(); });
    } finally { publishing = false; }
  }});
  api.copy = key => config[key];
  const cardVersions = new Map();
  let successTimer;
  function cardAction(variant, delta, weighted) {
    const version = (cardVersions.get(String(variant)) || 0) + 1;
    cardVersions.set(String(variant), version);
    return api.stepVariant(variant, delta, weighted).then(cart => {
      if (delta <= 0 || cardVersions.get(String(variant)) !== version) return;
      const notice = document.getElementById('qfc-add-success');
      const item = cart.items.find(i => String(i.variant_id || i.id) === String(variant) && !i.selling_plan_allocation);
      if (!notice || !item) return;
      notice.querySelector('[data-qfc-success-item]').textContent = item.quantity + ' × ' + (item.product_title || item.title);
      notice.hidden = false; clearTimeout(successTimer);
      successTimer = setTimeout(() => { if (!notice.matches(':hover') && !notice.contains(document.activeElement)) notice.hidden = true; }, 4000);
    }).catch(() => {});
  }
  api.cardStep = (controller, delta) => cardAction(controller.variant, delta, controller.qty_gram !== false);
  window.QFCart = api;
  // Searchanise has an independent legacy button listener. One capture owner prevents
  // it from disabling the second tap or sending a duplicate write beside this queue.
  window.addEventListener('click', event => {
    const button = event.target.closest?.('button.qty-plus,button.qty-minus');
    const wrapper = button?.closest('[data-controller~="addtocart"]');
    if (!wrapper?.closest('li.snize-product')) return;
    const variant = wrapper.querySelector('[data-target="addtocart.variant"]')?.value || wrapper.id.match(/^addToCart-(\d+)$/)?.[1];
    if (!/^\d+$/.test(variant || '')) return;
    event.preventDefault(); event.stopImmediatePropagation();
    const delta = button.classList.contains('qty-minus') ? -1 : 1;
    const weighted = !!wrapper.querySelector('[data-target="addtocart.qty_gram"]');
    void cardAction(variant, delta, weighted);
  }, true);
  let nextPage = null;
  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (!link || !api.pending() || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target || link.hasAttribute('download')) return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || (url.pathname === location.pathname && url.search === location.search)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    // A collection may have been prefetched before the pending add completed.
    // Use a fresh document request after saving, rather than that older basket snapshot.
    url.searchParams.set('_qfc', Date.now().toString(36));
    nextPage = url.href;
    void api.flush().then(() => { if (nextPage) { const destination = nextPage; nextPage = null; location.assign(destination); } }).catch(() => { nextPage = null; });
  }, true);
  function boot() {
    if (window.jQuery) window.jQuery(document).on('cart.requestComplete.qfcSync', (event, cart) => { if (!publishing && !api.pending() && cart?.items) api.seed(cart); });
    let renderFrame;
    const schedule = () => { if (!renderFrame) renderFrame = requestAnimationFrame(() => { renderFrame = null; api.render(); }); };
    const main = document.getElementById('MainContent');
    if (main) new MutationObserver(changes => {
      if (changes.some(change => Array.from(change.addedNodes).some(node => node.nodeType === 1 && (node.matches('[data-controller~="addtocart"]') || node.querySelector('[data-controller~="addtocart"]'))))) schedule();
    }).observe(main,{childList:true,subtree:true});
    if (window.jQuery) window.jQuery(document).on('Searchanise.ResultsUpdated.qfcSync Searchanise.Loaded.qfcSync', schedule);
    document.addEventListener('Searchanise.ResultsUpdated', schedule);
    api.render();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.dispatchEvent(new CustomEvent('qfc:ready'));
  document.querySelector('[data-qfc-dismiss]')?.addEventListener('click', () => { document.getElementById('qfc-add-success').hidden = true; });
})();
